import { useState, useEffect } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimePicker } from "@/components/ui/time-picker";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";

export const EditEventDialog = ({ open, onOpenChange, event, onUpdateEvent }) => {
  const [formData, setFormData] = useState({
    name: "",
    date: undefined,
    time: "12:00",
    location: null,
    attendees: "",
    description: ""
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name || "",
        date: event.date ? parse(event.date, "yyyy-MM-dd", new Date()) : undefined,
        time: event.time || "12:00",
        location: event.location ? {
          name: event.location.name,
          address: event.location.address,
          location: event.location.location || { lat: event.location.lat, lng: event.location.lng },
          placeId: event.location.place_id
        } : null,
        attendees: event.attendees?.toString() || "",
        description: event.description || ""
      });
    }
  }, [event]);

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date });
    setCalendarOpen(false);
  };

  const handleLocationChange = (place) => {
    setFormData({ ...formData, location: place });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Location is now optional
    if (!formData.name || !formData.date || !formData.time) {
      toast.error("Please fill in event name, date, and time (location is optional)");
      return;
    }

    const updatedEvent = {
      ...event,
      name: formData.name,
      date: format(formData.date, "yyyy-MM-dd"),
      time: formData.time,
      venue: formData.location?.name || '',
      venueAddress: formData.location?.address || '',
      venueLocation: formData.location?.location || null,
      venuePlaceId: formData.location?.placeId || '',
      attendees: parseInt(formData.attendees) || 0,
      description: formData.description
    };

    onUpdateEvent(updatedEvent);
    toast.success(`Event "${formData.name}" updated successfully!`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-2xl font-bold gradient-text">Edit Event</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-3 overflow-y-auto pr-2 flex-1">
            <div>
              <Label htmlFor="eventName">Event Name *</Label>
              <Input
                id="eventName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Mehendi Ceremony"
                className="mt-1.5"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1.5",
                        !formData.date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date ? format(formData.date, "MM/dd/yyyy") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={handleDateSelect}
                      defaultMonth={formData.date || new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="time">Time *</Label>
                <TimePicker 
                  value={formData.time}
                  onChange={(time) => setFormData({ ...formData, time })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="location">Location *</Label>
              <div className="mt-1.5">
                <GooglePlacesAutocomplete
                  value={formData.location}
                  onChange={handleLocationChange}
                  placeholder="Search for event location..."
                  types={['establishment']}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="attendees">Expected Attendees</Label>
              <Input
                id="attendees"
                type="number"
                value={formData.attendees}
                onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                placeholder="Number of guests"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Event details, notes, etc."
                rows={3}
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="gradient-primary">
              Update Event
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditEventDialog;
